import { Modal } from "components";
import { ButtonPrimary, ButtonSecondary } from "components/Atoms/Button";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import BookkeepingDropdown from "components/dropdowns/reactselect/BookkeepingDropdown";
import { LabelledRow } from "../../components";
import ValidatorContext from "formvalidation/FormContext";
import { useValidators } from "formvalidation/hooks";
import React, { useState } from "react";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import SubLabel from "components/Atoms/Sublabel";

interface Props {
  configuration: server.dto.InvoiceExportConfig
  isLoadingConfiguration: boolean
  onConfigurationChange: (config: server.dto.InvoiceExportConfig) => void
  availableBookkeepings: server.dto.BookkeepingSmall[]
  bookkeeping: server.dto.BookkeepingSmall
  onBookkeepingChange: (bookkeeping: server.dto.BookkeepingSmall) => void
}

export const ConfigureExport = (props: Props) => {
  const [configureExportModalOpen, setConfigureExportModalOpen] = useState(false);

  const onBookkeepingChange = (bookkeeping: server.dto.BookkeepingSmall) => {
    props.onBookkeepingChange(bookkeeping);
  }

  const onConfigurationChange = (config: server.dto.InvoiceExportConfig) => {
    props.onConfigurationChange(config);
    setConfigureExportModalOpen(false);
  }

  return <>
    <Modal
      isOpen={configureExportModalOpen}
      handleClose={() => setConfigureExportModalOpen(false)}
      title={RESX.InvoiceExport.ConfigureInvoiceExport}
      width="small"
    >
      <ConfigureExportModal
        configuration={props.configuration}
        onConfigurationChange={onConfigurationChange}
        bookkeeping={props.bookkeeping}
      />
    </Modal>
    <div className="s1_panel flex gap">
      <div style={{
        alignSelf: "center",
        width: 140,
      }} className="text-light">{RESX.Bookkeeping.resxBookkeeping}</div>
      <div className="grow1">
        <BookkeepingDropdown
          options={props.availableBookkeepings}
          value={props.bookkeeping}
          onChange={onBookkeepingChange}
        />
      </div>
      <div>
        <ButtonSecondary
          label={RESX.GeneralLabels.Configure}
          onClick={() => setConfigureExportModalOpen(true)}
        />
      </div>
    </div>
  </>
}

interface ConfigureExportModalProps {
  configuration: server.dto.InvoiceExportConfig
  onConfigurationChange: (config: server.dto.InvoiceExportConfig) => void
  bookkeeping: server.dto.BookkeepingSmall
}

const ConfigureExportModal = (props: ConfigureExportModalProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [period, setPeriod] = useState(props.configuration.period);
  const [addresseeKeyType, setAddresseeKeyType] = useState(props.configuration.addresseeKeyType);
  const [customValues, setCustomValues] = useState(props.configuration.customValues);
  const validators = useValidators();

  const onCustomValueChange = (key: string, value: string) => {
    const newCustomValues = customValues.map(cv => {
      if (cv.key === key) {
        return { key, value }
      };
      return cv;
    });
    setCustomValues(newCustomValues);
  }

  const saveConfiguration = async () => {
    setIsSaving(true);

    const validationResult = await validators.validateAll();
    if (validationResult.hasErrors) {
      setIsSaving(false);
    }

    const updateConfigDto: server.dto.ConfigureInvoiceExportInstruction = {
      bookkeepingId: props.bookkeeping.bookkeepingId,
      groupByPeriod: period,
      addresseeKeyType,
      customValues,
    }

    await window.CoreApi.InvoiceExport.ConfigureInvoiceExport(updateConfigDto);
    setIsSaving(false);
    props.onConfigurationChange({
      ...props.configuration,
      period,
      addresseeKeyType,
      customValues
    });
  }

  const labelWidth = 220;

  return <>
    <div className="scrollbox-auto">
      <ValidatorContext settings={validators.validatorSettings}>
        <table className="s1_table bordergrid">
          <tbody>
            <LabelledRow label={RESX.Bookkeeping.resxBookkeeping} bigPadding labelWidth={labelWidth}>
              <div>{props.bookkeeping.name}</div>
              <SubLabel>{props.bookkeeping.code}</SubLabel>
            </LabelledRow>

            <LabelledRow label={RESX.InvoiceExport.ExportMask} bigPadding labelWidth={labelWidth}>
              {props.configuration.exportMask.name}
            </LabelledRow>

            <LabelledRow label={RESX.DateTime.Period} bigPadding labelWidth={labelWidth}>
              <RadioGroup
                orientation="vertical"
                options={[{
                  label: RESX.DateTime.Quarter,
                  value: "quarter"
                }, {
                  label: RESX.DateTime.Month,
                  value: "month"
                }]}
                value={period}
                onChange={p => setPeriod(p)}
              />
            </LabelledRow>

            <LabelledRow label={RESX.InvoiceExport.AddresseeKey} bigPadding labelWidth={labelWidth}>
              <RadioGroup
                orientation="vertical"
                options={[{
                  label: RESX.InvoiceExport.UseSystemOneId,
                  value: "systemOneId"
                }, {
                  label: RESX.InvoiceExport.UseCustomRelationNumber,
                  value: "internalId"
                }]}
                value={addresseeKeyType}
                onChange={a => setAddresseeKeyType(a)}
              />
            </LabelledRow>

            {customValues.map(customValue => <LabelledRow key={customValue.key} label={customValue.key} labelWidth={labelWidth}>
              <OptionalTextInput
                maxLength={100}
                value={customValue.value}
                onChange={v => onCustomValueChange(customValue.key, v)}
              />
            </LabelledRow>)}

          </tbody>
        </table>
      </ValidatorContext>
    </div>
    <div className="flex">
      <div className="grow1"></div>
      <ButtonPrimary
        loading={isSaving}
        disabled={isSaving}
        label={RESX.Buttons.btnSave}
        onClick={saveConfiguration}
      />
    </div>
  </>
}
